// src/context/DataCacheProvider.jsx
import React, { createContext, useContext, useRef, useMemo } from 'react';

const DataCacheCtx = createContext(null);
const STORAGE_PREFIX = 'dataCache:';

// Helper to safely use localStorage
const storage = {
  get(key) {
    try {
      const item = localStorage.getItem(STORAGE_PREFIX + key);
      if (!item) return null;
      const { data, ts, ttl } = JSON.parse(item);
      if (Date.now() - ts < ttl) {
        return { data, ts };
      }
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    } catch (e) {
      return null;
    }
  },
  set(key, data, ttl) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({ data, ts: Date.now(), ttl }));
    } catch (e) {
      console.warn('Cache write error:', e);
    }
  }
};

export function DataCacheProvider({ children }) {
  const store = useRef(new Map());
  const inflight = useRef(new Map());

  const api = useMemo(() => ({
    get(key) {
      // Check memory first
      const memHit = store.current.get(key);
      if (memHit) return memHit.data;
      
      // Check localStorage
      const diskHit = storage.get(key);
      if (diskHit) {
        store.current.set(key, diskHit);
        return diskHit.data;
      }
      return undefined;
    },
    
    set(key, data, ttl = 60_000) {
      const entry = { data, ts: Date.now() };
      store.current.set(key, entry);
      storage.set(key, data, ttl);
      return data;
    },
    
    async use(key, loader, { ttlMs = 60_000 } = {}) {
      const now = Date.now();
      
      // Check memory cache
      const memHit = store.current.get(key);
      if (memHit && now - memHit.ts < ttlMs) return memHit.data;
      
      // Check localStorage
      const diskHit = storage.get(key);
      if (diskHit && now - diskHit.ts < ttlMs) {
        store.current.set(key, diskHit);
        return diskHit.data;
      }

      // Collapse concurrent calls
      if (inflight.current.has(key)) return inflight.current.get(key);

      const p = Promise.resolve()
        .then(loader)
        .then((data) => {
          api.set(key, data, ttlMs);
          return data;
        })
        .finally(() => inflight.current.delete(key));

      inflight.current.set(key, p);
      return p;
    },
    
    async preload(entries = []) {
      await Promise.all(entries.map(({ key, loader, ttlMs }) => api.use(key, loader, { ttlMs: ttlMs || 0 })));
    },
    
    clear() {
      store.current.clear();
      inflight.current.clear();
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(STORAGE_PREFIX)) localStorage.removeItem(key);
        });
      } catch (e) {}
    }
  }), []);

  return <DataCacheCtx.Provider value={api}>{children}</DataCacheCtx.Provider>;
}

export function useDataCache() {
  const ctx = useContext(DataCacheCtx);
  if (!ctx) throw new Error('useDataCache must be used inside <DataCacheProvider>');
  return ctx;
}